import { T, useT } from "@transifex/react";
import { router, useFocusEffect } from "expo-router";
import type { ParkWelcomePOSTErrorResponse } from "ipctg-inflow-types/InFlowParkAPI";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import uuid from "react-native-uuid";

import { CustomThemeContext, type CustomThemeContextData } from "@/contexts/CustomThemeContext";
import { toGluestackTheme } from "@/contexts/defaultTheme";
import { useAuth } from "@/hooks/useAuth";
import { useSessionsData } from "@/hooks/useSessions";
import useStorageReset from "@/hooks/useStorageReset";
import { useValidPasses } from "@/hooks/useValidPasses";
import { useWelcomeData } from "@/hooks/useWelcomeData";
import { fetchPlate } from "@/services/plate.queries";
import { saveToStorage } from "@/utils/secureStore";

import Parking from "@/assets/icons/Parking";
import Ticket from "@/assets/icons/Ticket";

import Screen from "@/components/layout/Screen";
import { ActiveFlexPassCard } from "@/components/shared/ActiveFlexPassCard";
import { ActiveSessionCard } from "@/components/shared/ActiveSessionCard";
import AppButton from "@/components/shared/AppButton";
import { Card } from "@/components/shared/Card";
import { GarageHeader, GarageHeaderSkeleton } from "@/components/shared/GarageHeader";
import ShowAddressModal from "@/components/shared/ShowAddressModal";
import { Box } from "@/components/ui/box";
import { Pressable } from "@/components/ui/pressable";
import { Skeleton, SkeletonText } from "@/components/ui/skeleton";
import { Text } from "@/components/ui/text";
import { VStack } from "@/components/ui/vstack";
import { useRefreshOnFocus } from "@/hooks/useRefreshOnFocus";
import { getPhoneDigits } from "@/utils/formatPhoneNumber";
import { UIModule } from "ipctg-inflow-types/InFlowEnums";

interface ErrorScreenProps {
  errorMessage: string;
  onRetry: () => void;
}

interface UserActionsProps {
  hasUser: boolean;
  hasFlexPass: boolean;
}

const errorMessages: Record<string, string> = {
  Unauthorized: "You are not authorized to access this service.",
  SystemError: "There was an issue with the system. Please try again later.",
  InvalidRequestFormat: "The request format is invalid. Please check the data.",
  QRNotFound: "QR Code not found. Please verify the QR Code and try again.",
  QRNotActive: "QR Code is not active. Please contact support.",
};

const ErrorScreen = ({ errorMessage, onRetry }: ErrorScreenProps) => {
  const t = useT();
  return (
    <Screen>
      <Box className="flex-1 justify-center items-center">
        <Text className="text-center text-base mb-4">
          <T _str={errorMessage} />
        </Text>
        <AppButton onPress={onRetry} title={t("Retry")} />
      </Box>
    </Screen>
  );
};

const UserActions = ({ hasUser, hasFlexPass }: UserActionsProps) => {
  const t = useT();
  return (
    <>
      {hasUser ? (
        <Card className="gap-[24px] pt-[32px] pb-[24px] px-[20px]">
          <Text className="text-typography-900 text-2xl font-bold  leading-[29.05px] text-center">
            <T _str="Welcome" />
          </Text>
          <AppButton onPress={() => router.push("/purchase/singlepass")} title={t("Buy Parking")} icon={Parking} />
          {hasFlexPass && <AppButton onPress={() => router.push("/purchase/flex-park")} title={t("Buy FlexPark")} icon={Ticket} />}
        </Card>
      ) : (
        <>
          <Card className="gap-[24px] pt-[32px] pb-[24px] px-[20px]">
            <Text className="text-typography-900 text-2xl font-bold  leading-[29.05px] text-center">
              <T _str="Welcome" />
            </Text>
            <AppButton onPress={() => router.push("/purchase/singlepass")} title={t("Buy Parking")} icon={Parking} />
            {hasFlexPass && <AppButton onPress={() => router.push("/purchase/flex-park")} title={t("Buy FlexPark")} icon={Ticket} />}
          </Card>

          <Pressable onPress={() => router.push("/auth/sign-in")}>
            <Text className="text-typography-900 text-sm leading-[20px] text-center">
              <T _str="Already a FlexPark Member?" />{" "}
              <Text className="text-sm font-bold underline decoration-solid text-typography-900">
                <T _str="Sign In" />
              </Text>{" "}
              <T _str="Now." />
            </Text>
          </Pressable>
        </>
      )}
    </>
  );
};

const WelcomeSkeleton = () => (
  <Screen>
    <GarageHeaderSkeleton />
    <VStack className="gap-5">
      <Card className="gap-[24px] pt-[32px] pb-[24px] px-[20px] items-center">
        <SkeletonText className="h-[30px] w-[150px] rounded-md" />
        <Skeleton className="h-[52px] w-full rounded-lg" />
        <Skeleton className="h-[52px] w-full rounded-lg" />
      </Card>
    </VStack>
  </Screen>
);

export default function Welcome() {
  const [isExtendLoading, setIsExtendLoading] = useState(false);
  const [isAddressModalVisible, setIsAddressModalVisible] = useState(false);
  const { data, isLoading: isWelcomeLoading, isError, error, refetch } = useWelcomeData();
  const { setCustomTheme } = useContext(CustomThemeContext);
  const { data: sessionData, isFetching: isSessionsPending, refetch: sessionRefetch, enableQuery: sessionEnableQuery } = useSessionsData();
  const { data: validPassesData, isFetching: isValidPassesPending, refetch: validPassesRefetch } = useValidPasses();
  const { user, isLoading: isAuthLoading } = useAuth();
  const t = useT();

  const hasFlexPass = data?.uiModules?.includes(UIModule.FlexPass) ?? false;
  const hasSinglePass = data?.uiModules?.includes(UIModule.SinglePurchase) ?? false;

  const skin = useMemo(() => (data && "skin" in data ? data.skin : null), [data]);

  const failReason = error && "failReason" in error ? (error as ParkWelcomePOSTErrorResponse).failReason : null;
  const isErrorState = failReason ?? isError;

  const handleRetry = useCallback(() => {
    router.replace(`/?${uuid.v4()}`);
  }, []);

  useStorageReset();
  useRefreshOnFocus(refetch);
  useRefreshOnFocus(sessionRefetch, sessionEnableQuery);
  useFocusEffect(
    useCallback(() => {
      if (user?.idToken) {
        validPassesRefetch();
      }
    }, [validPassesRefetch, user?.idToken]),
  );

  useEffect(() => {
    if (data?.skin.theme) {
      setCustomTheme((prevTheme: CustomThemeContextData) => ({
        ...prevTheme,
        skin: data.skin,
        theme: toGluestackTheme(data.skin.theme),
        mode: prevTheme.mode,
      }));
    }
  }, [data, setCustomTheme]);

  if (isAuthLoading || isExtendLoading || isSessionsPending || isValidPassesPending || isWelcomeLoading) {
    return <WelcomeSkeleton />;
  }

  if (isErrorState || !data) {
    const errorMessage = failReason ? t(errorMessages[failReason]) : t("An unknown error occurred.");
    return <ErrorScreen errorMessage={errorMessage} onRetry={handleRetry} />;
  }

  const handleExtendSession = async (sessionID: string, sessionPlate: any) => {
    try {
      setIsExtendLoading(true);
      if (sessionPlate) {
        await fetchPlate({ plate: sessionPlate }).catch((error) => console.error("Error fetching plate:", error));
      }

      if (user?.phone) {
        await saveToStorage("contact", `+${getPhoneDigits(user.phone)}`);
      }

      router.push({
        pathname: "/selectrate",
        params: {
          canExtend: "true",
          sessionID: sessionID,
        },
      });
    } catch (error) {
      console.error("Error processing session extension:", error);
    } finally {
      setIsExtendLoading(false);
    }
  };

  const openAddressModal = () => {
    setIsAddressModalVisible(true);
  };
  const closeAddressModal = () => {
    setIsAddressModalVisible(false);
  };

  return (
    <Screen>
      {skin && <GarageHeader locationName={skin.homepageHeader} onInfoPress={openAddressModal} />}
      <VStack className="gap-5">
        {(sessionData?.parkingSessions.length || validPassesData) && (
          <VStack className="gap-5">
            {hasSinglePass &&
              sessionData?.parkingSessions &&
              sessionData.parkingSessions.length > 0 &&
              sessionData.parkingSessions.map(({ session, canExtend }) => (
                <ActiveSessionCard
                  key={session.sessionID}
                  session={session}
                  canExtend={canExtend}
                  onExtend={(sessionID) => handleExtendSession(sessionID, session.plate)}
                />
              ))}
            {hasFlexPass &&
              validPassesData &&
              validPassesData.passes.length > 0 &&
              validPassesData.passes.map((pass) => (
                <ActiveFlexPassCard
                  key={pass.passID}
                  name={pass.passName}
                  usedPasses={pass.uses}
                  maxPassUses={pass.maxUses}
                  passNotes={pass.passNotes}
                  passID={pass.passID}
                />
              ))}
          </VStack>
        )}
        <UserActions hasUser={!!user} hasFlexPass={hasFlexPass} />
      </VStack>

      <ShowAddressModal
        isOpen={isAddressModalVisible}
        onClose={closeAddressModal}
        locationName={skin?.locationName ?? t("Unknown location")}
        locationAddress={skin?.locationAddress?.address ?? t("Address not available")}
      />
    </Screen>
  );
}
